import { motion } from "framer-motion";
import { Building2, Users, Scale, Heart, ArrowRight } from "lucide-react";

const ABOUT_IMG = "https://static.prod-images.emergentagent.com/jobs/1304f033-c917-4974-91b0-9d4153e7c191/images/0b589b57185c8cb4fafecf0fbc387c57711f5ff01ef836e395b97ad078fb42b9.jpeg";

export default function About() {
  return (
    <div>
      <section className="bg-gradient-to-b from-sky-50 to-white py-16">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 grid lg:grid-cols-2 gap-12 items-center">
          <div>
            <span className="text-xs font-bold uppercase tracking-[0.2em] text-sky-500">À propos</span>
            <h1 className="font-heading font-black text-4xl sm:text-5xl lg:text-6xl text-slate-900 mt-2">
              L'ASBL derrière Kwarizmix
            </h1>
            <p className="mt-6 text-lg text-slate-600 leading-relaxed">
              Kwarizmix est un projet porté par l'ASBL KINÉNIUM, dont la mission est
              de promouvoir les nouvelles technologies et de démocratiser la culture
              numérique auprès des enfants, de manière ludique, inclusive et responsable.
            </p>
          </div>
          <motion.div initial={{ opacity: 0, scale: 0.95 }} animate={{ opacity: 1, scale: 1 }} className="rounded-[2rem] overflow-hidden shadow-2xl shadow-sky-500/20 -rotate-2">
            <img src={ABOUT_IMG} alt="L'equipe Kwarizmix" className="w-full h-[360px] object-cover" />
          </motion.div>
        </div>
      </section>

      {/* Values */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12 grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
        {[
          { icon: Heart, title: "Pédagogie bienveillante", desc: "Chaque enfant progresse à son rythme, dans la confiance.", c: "bg-rose-100 text-rose-600" },
          { icon: Users, title: "Inclusion", desc: "Un accès au numérique pour toutes et tous, sans exception.", c: "bg-emerald-100 text-emerald-600" },
          { icon: Scale, title: "Responsabilité", desc: "Former des citoyens numériques éthiques et critiques.", c: "bg-sky-100 text-sky-600" },
        ].map((v, i) => (
          <motion.div
            key={v.title}
            initial={{ opacity: 0, y: 24 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.08 }}
            className="bg-white rounded-3xl border border-slate-100 shadow-sm p-7"
          >
            <span className={`grid place-items-center w-14 h-14 rounded-2xl mb-4 ${v.c}`}>
              <v.icon className="w-7 h-7" />
            </span>
            <h3 className="font-heading font-extrabold text-lg text-slate-900">{v.title}</h3>
            <p className="text-sm text-slate-500 mt-2">{v.desc}</p>
          </motion.div>
        ))}
      </section>

      {/* Pourquoi Kwarizmix */}
      <section className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 py-8" data-testid="pourquoi-kwarizmix">
        <div className="bg-sky-50 rounded-3xl border border-sky-100 p-8 sm:p-10">
          <h2 className="font-heading font-black text-3xl text-slate-900 mb-5">Pourquoi Kwarizmix ?</h2>
          <div className="space-y-4 text-slate-700 text-lg leading-relaxed">
            <p>
              Le nom Kwarizmix est un clin d'œil à Al-Khwârizmî, grand mathématicien et savant du IX<sup>e</sup> siècle.
              Son nom, latinisé en Algoritmi, est notamment à l'origine du mot «&nbsp;algorithme&nbsp;».
            </p>
            <p>
              Ce choix fait écho à l'ambition de Kwarizmix : donner aux enfants les clés pour comprendre le numérique,
              développer leur logique, leur créativité et apprendre à résoudre des problèmes pas à pas.
            </p>
            <p>
              Kwarizmix relie ainsi un héritage scientifique majeur aux compétences numériques d'aujourd'hui, avec une
              approche ludique, accessible et adaptée aux enfants.
            </p>
          </div>
          <a
            href="https://fr.wikipedia.org/wiki/Al-Khw%C3%A2rizm%C3%AE"
            target="_blank"
            rel="noreferrer"
            data-testid="wikipedia-link"
            className="inline-flex items-center gap-2 mt-6 text-sky-600 font-bold hover:gap-3 transition-all"
          >
            Découvrir Al-Khwârizmî sur Wikipédia <ArrowRight className="w-5 h-5" />
          </a>
        </div>
      </section>

      {/* Legal */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-12">
        <div className="bg-slate-900 rounded-[2rem] p-8 sm:p-12">
          <div className="flex items-center gap-3 mb-6">
            <span className="grid place-items-center w-12 h-12 rounded-2xl bg-slate-800 text-sky-400">
              <Building2 className="w-6 h-6" />
            </span>
            <h2 className="font-heading font-extrabold text-2xl text-white">Mentions légales</h2>
          </div>
          <div className="grid sm:grid-cols-2 gap-6 text-slate-300">
            <div>
              <p className="text-sm text-slate-500 font-semibold">Structure juridique</p>
              <p className="font-bold text-white">ASBL KINÉNIUM</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-semibold">Numéro d'entreprise (BCE)</p>
              <p className="font-bold text-white">0476.440.046</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-semibold">Siège social</p>
              <p className="font-bold text-white">Avenue de l'Équerre 27, 1348 Louvain-la-Neuve, Belgique</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-semibold">Tribunal</p>
              <p className="font-bold text-white">Tribunal de l'entreprise du Brabant wallon</p>
            </div>
            <div className="sm:col-span-2">
              <p className="text-sm text-slate-500 font-semibold">Objet social</p>
              <p className="font-bold text-white">La promotion des nouvelles technologies en Belgique : conseil, formation, promotion et création de projets technologiques.</p>
            </div>
            <div>
              <p className="text-sm text-slate-500 font-semibold">Contact</p>
              <p className="font-bold text-white">info@kwarizmix.be</p>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}
